import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  limit, 
  Timestamp,
  addDoc,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { db, auth } from '../lib/firebase';

export interface AuditRecord {
  id?: string;
  auditType: 'executive_attachment' | 'worthiness_gap' | 'attachment_roi' | 'intimidation_delusion' | 'fair_play_cpe';
  title: string;
  diagnostic: string;
  summary: string;
  scoreAnxiety?: number;
  scoreAvoidance?: number;
  timestamp: Date;
}

export interface VettingProfile {
  id?: string;
  name: string;
  stage: 'Observation' | 'Testing' | 'Verified' | 'Restricted';
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  notes: string;
  createdAt: Date;
}

export interface Invariant {
  id?: string;
  category: string;
  rule: string;
  isActive: boolean;
  order: number;
}

export const dbService = {
  // User Profile
  async ensureUserProfile(userId: string, email: string | null, displayName: string | null, retryCount = 0): Promise<any> {
    if (!userId) return null;

    try {
      const userRef = doc(db, 'users', userId);
      // Use standard getDoc which leverages cache + server
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await setDoc(userRef, {
          email: email,
          displayName: displayName,
          joinedAt: Timestamp.now(),
          onboardingComplete: false
        });

        // Provision default invariants
        const invRef = collection(db, 'users', userId, 'invariants');
        const defaults = [
          { category: "Communication", rule: "Consistency over intensity. I do not reward silence with pursuit.", isActive: true, order: 0 },
          { category: "Transparency", rule: "I view compartmentalization of a partner's world as a structural risk.", isActive: true, order: 1 },
          { category: "Autonomy", rule: "My professional legacy is non-negotiable. I do not negotiate my growth.", isActive: true, order: 2 }
        ];
        for (const d of defaults) {
          await addDoc(invRef, d);
        }
      }
      return (await getDoc(userRef)).data();
    } catch (error) {
      // If it's an offline error and we haven't exhausted retries, wait and try again
      if (error instanceof Error && error.message.includes('offline') && retryCount < 3) {
        console.warn(`Firestore offline on attempt ${retryCount + 1}, retrying...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return this.ensureUserProfile(userId, email, displayName, retryCount + 1);
      }
      throw error;
    }
  },

  // Audits
  async saveAuditResult(userId: string, result: Omit<AuditRecord, 'id' | 'timestamp'>) {
    if (!userId) throw new Error("Unauthenticated");

    const auditRef = collection(db, 'users', userId, 'audits');
    await addDoc(auditRef, {
      ...result,
      timestamp: Timestamp.now()
    });
    
    // Automatically flag the diagnostic report for recalculation so the dashboard generates a new synthesis
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      diagnosticReport: "",
      updatedAt: Timestamp.now()
    }, { merge: true });
  },

  async getAllAudits(userId: string) {
    if (!userId) return [];

    const auditRef = collection(db, 'users', userId, 'audits');
    const q = query(auditRef, orderBy('timestamp', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
      };
    }) as AuditRecord[];
  },

  async getLatestAudit(userId: string) {
    if (!userId) return null;

    const auditRef = collection(db, 'users', userId, 'audits');
    const q = query(auditRef, orderBy('timestamp', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) return null;
    const data = querySnapshot.docs[0].data();
    return { 
      id: querySnapshot.docs[0].id, 
      ...data,
      timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
    } as AuditRecord;
  },

  // Vetting
  async getVettingProfiles(userId: string) {
    if (!userId) return [];

    const vettingRef = collection(db, 'users', userId, 'vetting');
    const q = query(vettingRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return { 
        id: doc.id, 
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
      };
    }) as VettingProfile[];
  },

  async addVettingProfile(userId: string, profile: Omit<VettingProfile, 'id' | 'createdAt'>) {
    if (!userId) throw new Error("Unauthenticated");

    const vettingRef = collection(db, 'users', userId, 'vetting');
    await addDoc(vettingRef, {
      ...profile,
      createdAt: Timestamp.now()
    });
  },

  async deleteVettingProfile(userId: string, id: string) {
    if (!userId) return;
    await deleteDoc(doc(db, 'users', userId, 'vetting', id));
  },

  async getVettingProfile(userId: string, id: string) {
    if (!userId || !id) return null;
    const docRef = doc(db, 'users', userId, 'vetting', id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
    } as VettingProfile;
  },

  // Invariants
  async getInvariants(userId: string) {
    if (!userId) return [];

    const invRef = collection(db, 'users', userId, 'invariants');
    const q = query(invRef, orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Invariant[];
  },

  async toggleInvariant(userId: string, id: string, isActive: boolean) {
    if (!userId) return;
    await updateDoc(doc(db, 'users', userId, 'invariants', id), { isActive });
  },

  async updateUserProfile(userId: string, updates: { displayName?: string; photoURL?: string; diagnosticReport?: string; lastReportDate?: Date | any; }) {
    if (!userId) throw new Error("Unauthenticated");
    const userRef = doc(db, 'users', userId);
    
    const cleanUpdates = { ...updates };
    if (cleanUpdates.lastReportDate instanceof Date) {
      cleanUpdates.lastReportDate = Timestamp.fromDate(cleanUpdates.lastReportDate);
    }
    
    await setDoc(userRef, {
      ...cleanUpdates,
      updatedAt: Timestamp.now()
    }, { merge: true });
  },

  async getUserProfile(userId: string) {
    if (!userId) return null;
    const userSnap = await getDoc(doc(db, 'users', userId));
    return userSnap.exists() ? userSnap.data() : null;
  }
};
